<script setup lang="ts">
/**
 * 费用 in the navbar, Tencent Cloud's: the link to 充值中心 and, under the
 * pointer, a panel (NavMenu) — the balance with 充值, the money pages with
 * what can still be invoiced or withdrawn, then 财务管理 itself. Pages
 * 财务设置 has switched off are left out. The figures are read each time it
 * opens.
 */
import { computed, ref, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { IconSync, IconList, IconStamp, IconSwap, IconHistory, IconUndo, IconToRight } from '@arco-design/web-vue/es/icon'
import { api, type BillingOverview, type FinanceOverview } from '@/api/client'
import { stamp } from '@/utils/format'
import { navPanel } from '@/utils/layout'
import { monthStart, shown } from '@/utils/wallet'
import NavMenu from './NavMenu.vue'
import NavWallet from './NavWallet.vue'

const { t } = useI18n()
const router = useRouter()

const bill = ref<BillingOverview | null>(null)
const fin = ref<FinanceOverview | null>(null)
const readAt = ref('')
async function load() {
  const [b, f] = await Promise.allSettled([api.billingOverview({ since: monthStart() }), api.financeOverview()])
  if (b.status === 'fulfilled') bill.value = b.value
  if (f.status === 'fulfilled') fin.value = f.value
  readAt.value = stamp(new Date().toISOString()).slice(0, 11)
}

interface Row {
  route: string
  label: string
  icon: Component
  value?: string
}
// Tencent Cloud's three groups: what is bought and what it left to claim,
// the records, the way into 财务管理. Its 优惠券 is our 可提现.
const groups = computed<Row[][]>(() => {
  const f = fin.value
  const rows: (Row | false | undefined)[][] = [
    [
      { route: 'my-packages', label: 'navbar.renewals', icon: IconSync },
      { route: 'finance-orders', label: 'navbar.myOrders', icon: IconList },
      f?.invoice.enabled && { route: 'finance-invoices', label: 'navbar.invoiceable', icon: IconStamp, value: shown(f.uninvoiced_cents) },
      f?.withdraw.enabled && { route: 'finance-withdraw', label: 'navbar.cashable', icon: IconSwap, value: shown(f.cashable_cents) },
    ],
    [
      { route: 'finance-ledger', label: 'ledger.title', icon: IconHistory },
      f?.refund.enabled && { route: 'finance-refunds', label: 'refund.title', icon: IconUndo },
    ],
    [{ route: 'billing', label: 'navbar.toFinance', icon: IconToRight }],
  ]
  return rows.map((g) => g.filter((r): r is Row => !!r))
})

function go(name: string) {
  navPanel.value = null
  router.push({ name })
}
</script>

<template>
  <NavMenu name="billing" route="billing" :label="t('navbar.billing')" @open="load">
    <div class="nav-panel">
      <div class="nav-panel-head">
        <NavWallet
          :balance-cents="bill?.balance_cents"
          :spent-cents="bill ? (bill.spent_since_cents ?? 0) : undefined"
          :read-at="readAt"
          @top-up="go('billing')"
        />
      </div>
      <div v-for="(g, i) in groups" :key="i" class="nav-panel-group">
        <button v-for="r in g" :key="r.route" type="button" class="nav-panel-row" @click="go(r.route)">
          <component :is="r.icon" />{{ t(r.label) }}
          <span v-if="r.value" class="nav-panel-value">{{ r.value }}</span>
        </button>
      </div>
    </div>
  </NavMenu>
</template>
